<template>
  <div>
    <CCardBody>
      <div class="d-flex gap-1 justify-content-end text-right btngroup">
        <CButton class="addBtn" @click="showModal">
          Create
        </CButton>
      </div>
      <search-items v-model="filters.search" :searchHandler="searchBrands" placeholder="Search name"></search-items>
      <b-table :items="brands" :fields="headers" hover sorter>
        <template #cell(name)="item">
          <span>{{ item.item.name }}</span>
        </template>
        <template #cell(royalty_percentage)="item">
          <span>{{ item.item.royalty_percentage }}%</span>
        </template>
        <template #cell(actions)="item">
          <edit class="delete_step ml-2 cursor-pointer" @click="editBrand(item.item)" fillColor="#009FE3" />
          <delete class="delete_step ml-2 cursor-pointer" @click="openDeleteModal(item.item)" fillColor="#E30000" />
        </template>
      </b-table>
      <b-pagination
        v-model="pagination.currentPage"
        :total-rows="pagination.rows"
        :per-page="pagination.perPage"
        aria-controls="brandList"
        align="center"
        @change="handlePagination"
        v-if="pagination.total > 0"
      ></b-pagination>
    </CCardBody>

    <!-- Create / Edit Brand Modal -->
    <b-modal id="brandModal" @hide="resetForm" size="md" no-close-on-backdrop hide-footer>
      <template #modal-title>
        <span>{{ modalTitle }}</span>
      </template>
      <b-form-group id="brand-name" label-for="brand-name">
        <CContainer>
          <CRow>
            <CCol col="12">
              <label>Name</label>
              <b-form-input v-model="brand.name" class="mb-4" placeholder="Enter brand name"></b-form-input>
              <label>Royalty Percentage</label>
              <b-form-input
                type="number"
                v-model.number="brand.royalty_percentage"
                class="mb-4"
                placeholder="Enter royalty percentage"
                min="0"
                max="100"
              ></b-form-input>
            </CCol>
          </CRow>
        </CContainer>
      </b-form-group>
      <CRow>
        <CCol col="12" class="text-right">
          <button @click="saveBrand" class="btn btn-primary">
            Save
          </button>
        </CCol>
      </CRow>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal id="deleteBrandModal" size="md" no-close-on-backdrop>
      <template #modal-title>
        <span>Delete Brand</span>
      </template>
      <h5>Are you sure you want to delete this brand?</h5>
      <template #modal-footer="{ hide }">
        <b-button size="md" variant="danger" @click="confirmDelete">Delete</b-button>
        <b-button size="md" @click="hide()">Cancel</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import myMixins from '@/mixins/myMixins';
import Delete from 'vue-material-design-icons/Delete.vue';
import Edit from 'vue-material-design-icons/Pencil.vue';
import SearchItems from '@/components/SearchItems';
import { http } from '@/config/httpCommon';

export default {
  components: { SearchItems, Delete, Edit },
  mixins: [myMixins],
  created() {
    this.getBrands();
  },
  data() {
    return {
      filters: {
        search: '',
        page: 1,
      },
      headers: [
        { key: 'name', label: 'Name' },
        { key: 'royalty_percentage', label: 'Royalty Percentage' },
        { key: 'actions', label: 'Actions' },
      ],
      brand: {
        name: '',
        royalty_percentage: 0,
      },
      mode: 'create',
      brandId: null,
      pagination: {
        rows: null,
        currentPage: null,
        perPage: null,
        total: 0,
      },
      brands: [],
    };
  },
  computed: {
    modalTitle() {
      if (this.mode === 'edit') {
        const brand = this.brands.find((b) => b.id === this.brandId);
        return brand ? `Editing brand ${brand.name}` : 'Edit Brand';
      }
      return 'Create Brand';
    },
    hasEmptyProperties() {
      const { name, royalty_percentage } = this.brand;
      return [name, royalty_percentage].some(
        (value) => value === '' || value === null || value === undefined
      );
    },
  },
  methods: {
    searchBrands() {
      this.getBrands(this.filters.search, 1);
    },
    getBrands(search = '', page = 1) {
      this.$store.commit('set', ['showLoader', true]);
      http
        .get(`brands?paginate=true&search=${search}&page=${page}`)
        .then((res) => {
          const result = res.data.result;
          // Handle the structure: { success: true, result: { data: [...], total: 5, ... } }
          if (result && result.data && !result.brands) {
            this.brands = result.data;
            this.makePagination(result);
          } 
          // Handle the structure: { success: true, result: { brands: { data: [...], ... } } }
          else if (result && result.brands) {
            this.brands = result.brands.data || result.brands;
            if (result.brands.data) this.makePagination(result.brands);
          } 
          // Handle flat array
          else if (Array.isArray(result)) {
            this.brands = result;
          }
          this.$store.commit('set', ['showLoader', false]);
        })
        .catch((err) => {
          this.$store.commit('set', ['showLoader', false]);
          this.handleResponseErrors(err);
        });
    },
    saveBrand() {
      if (this.hasEmptyProperties) {
        this.showToast('Please fill all fields', 'ERROR');
        return;
      }
      this.$store.commit('set', ['showLoader', true]);
      
      // Ensure royalty_percentage is sent as a number if needed, 
      // but usually the backend handles the strings from form-data/json.
      const payload = {
        ...this.brand,
        royalty_percentage: Number(this.brand.royalty_percentage)
      };

      const request =
        this.mode === 'edit'
          ? http.put(`brands/${this.brandId}`, payload)
          : http.post('brands', payload);

      request
        .then(() => {
          this.getBrands(this.filters.search, this.filters.page);
          this.$bvModal.hide('brandModal');
          this.$store.commit('set', ['showLoader', false]);
          this.showToast(
            this.mode === 'edit'
              ? 'Brand updated successfully'
              : 'Brand created successfully',
            'SUCCESS'
          );
        })
        .catch((err) => {
          this.$store.commit('set', ['showLoader', false]);
          this.handleResponseErrors(err);
        });
    },
    editBrand(item) {
      this.mode = 'edit';
      this.brandId = item.id;
      this.brand.name = item.name;
      this.brand.royalty_percentage = parseFloat(item.royalty_percentage) || 0;
      this.$bvModal.show('brandModal');
    },
    openDeleteModal(item) {
      this.brandId = item.id;
      this.$bvModal.show('deleteBrandModal');
    },
    confirmDelete() {
      this.$store.commit('set', ['showLoader', true]);
      http
        .delete(`brands/${this.brandId}`)
        .then(() => {
          this.getBrands(this.filters.search, this.filters.page);
          this.$store.commit('set', ['showLoader', false]);
          this.$bvModal.hide('deleteBrandModal');
          this.showToast('Brand deleted successfully', 'SUCCESS');
        })
        .catch((err) => {
          this.$store.commit('set', ['showLoader', false]);
          this.handleResponseErrors(err);
        });
    },
    showModal() {
      this.mode = 'create';
      this.$bvModal.show('brandModal');
    },
    resetForm() {
      this.brand = {
        name: '',
        royalty_percentage: 0,
      };
      this.mode = 'create';
      this.brandId = null;
    },
    async handlePagination(page = 1) {
      this.filters.page = page;
      this.getBrands(this.filters.search, this.filters.page);
    },
  },
};
</script>
